import jsPDF from 'jspdf';
import { formatDualVolumeWeight } from './unitConverter';

const fmt = (n) => n ? new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) : '0';

const loadImageAsBase64 = async (url) => {
  if (!url) return null;
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve({
        dataUrl: canvas.toDataURL('image/png'),
        width: img.width,
        height: img.height,
        aspectRatio: img.width / (img.height || 1)
      });
    };
    img.onerror = async () => {
      try {
        const resp = await fetch(url);
        const blob = await resp.blob();
        const reader = new FileReader();
        reader.onload = () => {
          const tempImg = new Image();
          tempImg.onload = () => {
            resolve({
              dataUrl: reader.result,
              width: tempImg.width,
              height: tempImg.height,
              aspectRatio: tempImg.width / (tempImg.height || 1)
            });
          };
          tempImg.onerror = () => resolve({ dataUrl: reader.result, width: 100, height: 100, aspectRatio: 1 });
          tempImg.src = reader.result;
        };
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      } catch {
        resolve(null);
      }
    };
    img.src = url;
  });
};

const generateRandomBatch = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 15; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

// Formatting Nomor SPK
// Format yang diminta: SK.06.2026.0002
// Mengambil urutan dari string invoice (misal INV-XXX-001 -> 001)
const generateSpkNumber = (invoiceCode, date) => {
  const d = new Date(date || new Date());
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  
  // Try to extract sequence from invoice code
  let seqStr = "0001";
  if (invoiceCode) {
    const parts = invoiceCode.split('-');
    const lastPart = parts[parts.length - 1];
    const numMatch = lastPart.match(/\d+/);
    if (numMatch) {
      seqStr = String(parseInt(numMatch[0])).padStart(4, '0');
    }
  }
  return `SK.${month}.${year}.${seqStr}`;
};

export const generateSpkPdf = async ({ invoiceCode, record, product, products, companySettings = {}, showNilai = false }) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const pageW = 210;
  const marginL = 15;
  const marginR = 15;
  const contentW = pageW - marginL - marginR;

  const logoUrl = '/logo.png';
  const logoInfo = await loadImageAsBase64(logoUrl);

  const productList = products || (product ? [product] : []);
  if (productList.length === 0) return;

  productList.forEach((prod, pIndex) => {
    if (pIndex > 0) {
      doc.addPage();
    }
    
    let y = 15;

    // ─── HEADER ──────────────────────────────────────────────────────────────────
    const logoSize = 14; // 14mm x 14mm (1:1 aspect ratio square, sama persis seperti Invoice)

    if (logoInfo?.dataUrl) {
      doc.addImage(logoInfo.dataUrl, 'PNG', marginL, y, logoSize, logoSize, '', 'FAST');
    } else {
      // Fallback Logo
      doc.setFillColor(180, 140, 80);
      doc.circle(marginL + 7, y + 7, 7, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('S', marginL + 5, y + 9.5);
    }

    // Nama Perusahaan (Di Samping Logo)
    const textStartX = marginL + logoSize + 4;
    const namaPerusahaan = 'PT. SATALA DERMATECH ESSENTIAL';
    const alamatPerusahaan = companySettings.alamat_perusahaan || 'Jalan aster kavling Paspampres, Kota Batu, Kec. Ciomas, Kabupaten Bogor, Jawa Barat';
    
    doc.setTextColor(30, 20, 10);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text(namaPerusahaan, textStartX, y + 5.5);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(100, 80, 60);
    const singleLineAlamat = alamatPerusahaan.replace(/\n/g, ' ').replace(', Indonesia', '');
    doc.text(singleLineAlamat, textStartX, y + 10.5, { maxWidth: contentW - logoSize - 5 });

    // Line Separator (Double Gold Elegant Line matching Invoice)
    y += 20;
    doc.setDrawColor(180, 140, 60);
    doc.setLineWidth(0.8);
    doc.line(marginL, y, pageW - marginR, y);
    doc.setLineWidth(0.3);
    doc.setDrawColor(220, 220, 220);
    doc.line(marginL, y + 0.8, pageW - marginR, y + 0.8);
    
    // ─── JUDUL: Pekerjaan Pesanan ──────────────────────────────────────────────────
    y += 10;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(140, 98, 8); // Gold Satala
    doc.text('Pekerjaan Pesanan', marginL, y);
    
    // ─── Info Brand, Varian, PIC (Kanan Atas) ──────────────────────────────────
    const brandLabel = (record.brand_name || record.client || record.nama_brand || 'Brand').split('/')[0].trim();
    const picLabel = record.pic_marketing || record.pic || record.pic_brand || ((record.client && record.client.includes('/')) ? record.client.split('/')[1]?.trim() : '-');
    const variantLabel = prod.product_name || prod.variant_name || prod.nama_product || prod.nama_varian || 'Varian';

    doc.setDrawColor(140, 98, 8);
    doc.setLineWidth(0.8);
    doc.line(marginL, y + 3, marginL + 75, y + 3); // Underline title

    // ─── INFO KOTAK KIRI & KANAN ─────────────────────────────────────────────────────────────
    y += 8;
    
    const boxH = 22;
    const leftBoxW = 85;
    const boxW = 85;
    const boxX = pageW - marginR - boxW;

    doc.setDrawColor(230, 213, 184); // Light gold border
    doc.setFillColor(249, 245, 236); // Very light gold bg

    // Kotak Kiri (Nomor, Tanggal, No. Batch)
    if (typeof doc.roundedRect === 'function') {
      doc.roundedRect(marginL, y, leftBoxW, boxH, 2, 2, 'FD');
    } else {
      doc.rect(marginL, y, leftBoxW, boxH, 'F');
    }

    // Kotak Kanan (Brand, Varian, PIC, Qty)
    if (typeof doc.roundedRect === 'function') {
      doc.roundedRect(boxX, y, boxW, boxH, 2, 2, 'FD');
    } else {
      doc.rect(boxX, y, boxW, boxH, 'F');
    }

    const batchCodeDisplay = prod.batch_number || record.batch_number || '-';
    const spkDateStr = prod.spk_date || record.spk_date || record.spk_printed_date || record.naik_produksi_date || record.createdAt || record.tanggal || new Date().toISOString();
    const spkNumber = generateSpkNumber(invoiceCode, spkDateStr) + (productList.length > 1 ? `-${pIndex + 1}` : '');
    const tanggalFormat = new Date(spkDateStr).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });

    // Teks Kotak Kiri
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(140, 98, 8);
    doc.text('Nomor SPK', marginL + 4, y + 5.5);
    doc.text('Tanggal', marginL + 4, y + 11);
    doc.text('No. Batch', marginL + 4, y + 16.5);

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(30, 30, 30);
    doc.text(`:  ${spkNumber}`, marginL + 22, y + 5.5);
    doc.text(`:  ${tanggalFormat}`, marginL + 22, y + 11);
    doc.setFont('helvetica', 'bold');
    doc.text(`:  ${batchCodeDisplay}`, marginL + 22, y + 16.5);

    // Teks Kotak Kanan
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(140, 98, 8);
    doc.text('Brand', boxX + 4, y + 5.5);
    doc.text('Varian', boxX + 4, y + 11);
    doc.text('PIC / Qty', boxX + 4, y + 16.5);

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 30);
    doc.text(`: ${brandLabel.slice(0, 30)}`, boxX + 18, y + 5.5);
    doc.text(`: ${variantLabel.slice(0, 30)}`, boxX + 18, y + 11);
    doc.setFont('helvetica', 'normal');
    const qtyTotalProduct = Number(prod.qty) || Number(prod.target_qty) || Number(record.final_qty) || Number(record.target_qty) || 1;
    const qtyPcsStr = `${qtyTotalProduct} pcs`;
    doc.text(`: ${picLabel.slice(0, 16)} (${qtyPcsStr})`, boxX + 18, y + 16.5);

    y += 28;

    // ─── TABEL BARANG (FORMULA + KEMASAN) ─────────────────────────────────────────
    const tableData = [];

    const addItemToTable = (nama, satuan, qty, harga, vendor, customQtyDisplay) => {
      if (nama && (harga > 0 || !showNilai)) {
        let displayNama = nama.toUpperCase();
        if (vendor && vendor.toUpperCase() === 'DARI CLIENT') {
           displayNama += ' (DARI CLIENT)';
        }
        
        let displaySatuan = (satuan || '').toUpperCase();

        tableData.push({
          kode: '-',
          nama: displayNama,
          qty: qty * qtyTotalProduct,
          customQtyDisplay: customQtyDisplay,
          satuan: displaySatuan,
          nilai: harga
        });
      }
    };

    if (prod.is_fullset && prod.fullset_nama) {
      addItemToTable(prod.fullset_nama, 'PCS', 1, prod.fullset_harga_beli || prod.fullset_harga || prod.hpp_kemasan || 0, prod.fullset_vendor);
    } else {
      if (prod.botol_nama) addItemToTable(prod.botol_nama, 'PCS', 1, prod.botol_harga_beli || prod.botol_harga || 0, prod.botol_vendor);
      if (prod.tutup_nama) addItemToTable(prod.tutup_nama, 'PCS', 1, prod.tutup_harga_beli || prod.tutup_harga || 0, prod.tutup_vendor);
      if (prod.spray_nama) addItemToTable(prod.spray_nama, 'PCS', 1, prod.spray_harga_beli || prod.spray_harga || 0, prod.spray_vendor);
    }

    // Fallback: Read from components if not present directly on prod
    const allComponents = prod.components || record.components || [];
    if (!prod.botol_nama && !prod.is_fullset && allComponents.length > 0) {
      allComponents.forEach(c => {
        const cat = (c.category || '').toLowerCase();
        const itemName = c.item_name || c.name || '';
        if (cat === 'botol' || cat === 'tutup' || cat === 'spray') {
          addItemToTable(itemName, 'PCS', 1, 0, c.vendor || c.status?.includes('client') ? 'DARI CLIENT' : '');
        } else if (cat === 'tambahan' || cat === 'box' || cat === 'kemasan_lainnya') {
          addItemToTable(itemName, 'PCS', c.qty_per_pcs || 1, 0, c.vendor);
        }
      });
    }

    if (prod.komponen_tambahan && prod.komponen_tambahan.length > 0) {
      prod.komponen_tambahan.forEach(k => {
        const itemNama = (k.nama || k.item_nama || k.nama_kategori || '').toLowerCase().trim();
        const itemKategori = (k.kategori || k.kategori_bahan || '').toLowerCase().trim();
        
        // Exclude Sablon dan Coloring / Pewarnaan dari SPK karena sudah include di botol
        const isSablonOrColoring = itemNama.includes('sablon') || 
                                   itemNama.includes('coloring') || 
                                   itemNama.includes('pewarnaan') || 
                                   itemKategori.includes('sablon') || 
                                   itemKategori.includes('coloring');
        
        if (!isSablonOrColoring) {
          addItemToTable(k.nama || k.item_nama || k.nama_kategori || 'ITEM TAMBAHAN', 'PCS', k.qty || 1, k.harga || k.harga_beli || 0, k.vendor);
        }
      });
    }

    let totalVolumeMl = prod.botol_ukuran_aktual || prod.botol_ukuran_client || prod.botol_ukuran_label || prod.ukuran_botol_ml || record.ukuran_botol_ml || 0;
    let formulaInfo = [];
    const rawRacikan = (prod.racikan && prod.racikan.length > 0) 
      ? prod.racikan 
      : (prod.racikan_digunakan && prod.racikan_digunakan.length > 0)
        ? prod.racikan_digunakan
        : (record.racikan_digunakan && record.racikan_digunakan.length > 0)
          ? record.racikan_digunakan
          : [];

    if (rawRacikan.length > 0) {
      if (!totalVolumeMl || totalVolumeMl === 0) {
        totalVolumeMl = 50; // Fallback jika ukuran ml belum tercatat
      }
      
      rawRacikan.forEach(r => {
        const persentase = parseFloat(String(r.persentase || '0').replace(',', '.')) || 0;
        let qtyFormulaTotal = (persentase / 100) * totalVolumeMl * qtyTotalProduct;

        // Support direct kebutuhan_nilai from ProductionOrder
        if ((!qtyFormulaTotal || qtyFormulaTotal === 0) && r.kebutuhan_nilai) {
          const sat = (r.kebutuhan_satuan || r.satuan || '').toLowerCase();
          if (sat === 'l' || sat === 'liter' || sat === 'kg') qtyFormulaTotal = Number(r.kebutuhan_nilai) * 1000;
          else qtyFormulaTotal = Number(r.kebutuhan_nilai);
        }
        
        const dual = formatDualVolumeWeight(
          qtyFormulaTotal, 
          r.kategori || r.kategori_bahan || r.nama_kategori, 
          r.bahan_nama || r.nama_bahan || r.nama || r.item_nama || r.item_name
        );
        let customQtyStr = `${dual.volumeText} / ${dual.weightText}`;
        let calcSatuan = '';
        let calcQty = dual.volumeMl >= 1000 ? dual.volumeMl / 1000 : dual.volumeMl;

        const hargaSatuan = r.harga_per_satuan || r.hpp_bahan || 0;

        let bahanKategori = (r.kategori || r.kategori_bahan) ? `${(r.kategori || r.kategori_bahan).toUpperCase()}` : '';
        const bahanNamaAsli = (r.bahan_nama || r.nama_bahan || r.nama || 'Formula Bahan').toUpperCase();
        
        if (bahanKategori === 'LAINNYA' || bahanNamaAsli.startsWith(bahanKategori)) {
          bahanKategori = '';
        } else if (bahanKategori) {
          bahanKategori += ' ';
        }

        const namaLengkap = bahanKategori + bahanNamaAsli;

        tableData.push({
          kode: '-',
          nama: namaLengkap,
          qty: calcQty,
          customQtyDisplay: customQtyStr,
          satuan: calcSatuan,
          nilai: hargaSatuan
        });

        const pctStr = r.persentase ? `${r.persentase}%` : '';
        formulaInfo.push(`${namaLengkap} = ${pctStr}`);
      });
    }

    const cols = [105, 25, 20, 30];
    const colX = cols.reduce((acc, w, i) => { acc.push(i === 0 ? marginL : acc[i - 1] + cols[i - 1]); return acc; }, []);
    const rowH = 7;

    doc.setFillColor(140, 98, 8); // Gold Satala
    doc.rect(marginL, y, contentW, rowH, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    const headers = ['Nama Barang', 'Kts.', 'Satuan', 'Nilai'];
    
    headers.forEach((h, i) => {
        if (h === 'Nilai') {
            doc.text(h, colX[i] + cols[i] - 3, y + 4.8, { align: 'right' });
        } else {
            doc.text(h, colX[i] + 2, y + 4.8);
        }
    });
    y += rowH;

    let totalBiaya = 0;
    let totalKts = 0;

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(10, 10, 10);
    tableData.forEach((row) => {
      if (y > pageW + 50) {
         doc.addPage();
         y = marginL;
      }
      
      doc.text(row.nama, colX[0] + 2, y + 4.8, { maxWidth: cols[0] - 4 });
      
      let formattedQty = '';
      if (row.customQtyDisplay) {
        formattedQty = row.customQtyDisplay;
      } else if (row.satuan === 'PCS') {
        formattedQty = new Intl.NumberFormat('id-ID', { maximumFractionDigits: 0 }).format(row.qty);
      } else {
        formattedQty = new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(row.qty);
      }
      doc.text(formattedQty, colX[1] + 2, y + 4.8);
      
      doc.text(row.satuan, colX[2] + 2, y + 4.8);
      
      let subtotal = 0;
      if (showNilai) {
          subtotal = row.qty * row.nilai;
      }
      doc.text(fmt(subtotal), colX[3] + cols[3] - 2, y + 4.8, { align: 'right', maxWidth: cols[3] - 4 });
      totalBiaya += subtotal;

      totalKts += row.qty;
      y += rowH;
    });

    doc.setDrawColor(140, 98, 8);
    doc.setLineWidth(0.8);
    doc.line(marginL, y, pageW - marginR, y);
    
    y += 4;
    const footerX = pageW - marginR - 60;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(50, 50, 50);
    doc.text('Total Barang', footerX, y + 4.8);
    doc.text(fmt(qtyTotalProduct), pageW - marginR - 2, y + 4.8, { align: 'right' });
    y += rowH;

    doc.text('Total Biaya', footerX, y + 4.8);
    doc.text(fmt(totalBiaya), pageW - marginR - 2, y + 4.8, { align: 'right' });
    y += rowH;

    doc.setFillColor(140, 98, 8);
    doc.rect(footerX - 2, y, 62, rowH, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.text('Total', footerX, y + 4.8);
    doc.text(fmt(totalBiaya), pageW - marginR - 2, y + 4.8, { align: 'right' });

    let ketY = y - (rowH * 2) + 2;
    doc.setTextColor(140, 98, 8);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    
    doc.text('Keterangan', marginL, ketY);
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(marginL, ketY + 1.5, marginL + 50, ketY + 1.5);
    ketY += 6;

    // Ubah warna kembali ke hitam pekat untuk isi keterangan
    doc.setTextColor(30, 30, 30);

    const productName = `${prod.nama_product || prod.botol_nama || 'Produk'}`;
    const keteranganText = `${productName.toUpperCase()} ${totalVolumeMl} ML ${qtyTotalProduct} PCS`;
    
    doc.text(keteranganText.slice(0, 60), marginL, ketY);
    ketY += 6;

    formulaInfo.forEach(info => {
      doc.text(info, marginL, ketY);
      ketY += 4;
    });

    ketY += 4;
    doc.text(`NO BATCH=${batchCodeDisplay}`, marginL, ketY);
    ketY += 4;
    
    const dProd = new Date(spkDateStr);
    dProd.setFullYear(dProd.getFullYear() + 3);
    const expDateStr = dProd.toLocaleDateString('id-ID', { day: '2-digit', month: '2-digit', year: 'numeric' });
    doc.text(`EXP DATE=${expDateStr}`, marginL, ketY);
    
    doc.setLineDashPattern([2, 2], 0);
    doc.line(marginL, ketY + 1.5, marginL + 60, ketY + 1.5);
    doc.setLineDashPattern([], 0);

  });

  // --- OUTPUT ---
  doc.autoPrint();
  window.open(doc.output('bloburl'), '_blank');
};
